import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { globalFetch } from 'foundation/js/api'

const SUPPORT_TYPE = Object.freeze({
    NONE: null,
    STAFET: 'Stafet',
    HOLD: 'Hold',
    DELTAGER: 'Deltager'
})

export default {
    name: 'FormStafetSelector',
    props: {
        elementName: {
            type: String,
            required: true
        },
        elementId: {
            type: String,
            required: true
        },
        fieldNames: {
            type: Object,
            required: true
        },
        labels: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const store = useStore()

        const isSubmitted = ref(false)

        // ── Stafet list & search ──────────────────────────────────
        const stafetter = ref([])
        const searchQuery = ref('')
        const selectedStafet = ref(null)   // { name, stafetKode }
        const showDropdown = ref(false)
        const searchContainer = ref(null)

        const filteredStafetter = computed(() => {
            const q = searchQuery.value.trim().toLowerCase()
            if (!q) return stafetter.value
            return stafetter.value.filter(s => s.name.toLowerCase().includes(q))
        })

        // ── Checkbox ──────────────────────────────────────────────
        const supportStafet = ref(false)

        // ── Stafetkode ────────────────────────────────────────────
        const stafetkode = computed(() => selectedStafet.value?.stafetKode ?? null)
        const hasInteractedStafet = ref(false)
        const isStafetkodeValid = computed(() => !supportStafet.value || !!stafetkode.value)
        const showStafetError = computed(() => (isSubmitted.value || hasInteractedStafet.value) && !isStafetkodeValid.value)

        // ── Support type ──────────────────────────────────────────
        const supportTypeRadioOptions = [
            { value: SUPPORT_TYPE.STAFET,   label: props.labels.supportTypeOptionStafet   },
            { value: SUPPORT_TYPE.HOLD,     label: props.labels.supportTypeOptionHold     },
            { value: SUPPORT_TYPE.DELTAGER, label: props.labels.supportTypeOptionDeltager }
        ]

        const supportType = ref(SUPPORT_TYPE.STAFET)
        const hasInteractedSupportType = ref(false)
        const isSupportTypeValid = computed(() => !supportStafet.value || !!supportType.value)
        const showSupportTypeError = computed(() => (isSubmitted.value || hasInteractedSupportType.value) && !isSupportTypeValid.value)

        // ── Hold ──────────────────────────────────────────────────
        const hold = ref([])
        const holdkode = ref(null)
        const hasInteractedHold = ref(false)
        const needsHold = computed(() =>
            supportType.value === SUPPORT_TYPE.HOLD ||
            supportType.value === SUPPORT_TYPE.DELTAGER
        )
        const isHoldkodeValid = computed(() => !supportStafet.value || !needsHold.value || !!holdkode.value)
        const showHoldError = computed(() => (isSubmitted.value || hasInteractedHold.value) && !isHoldkodeValid.value)

        // ── Deltager ──────────────────────────────────────────────
        const deltagere = ref([])
        const deltagerId = ref(null)
        const hasInteractedDeltager = ref(false)
        const needsDeltager = computed(() => supportType.value === SUPPORT_TYPE.DELTAGER)
        const isDeltagerIdValid = computed(() => !supportStafet.value || !needsDeltager.value || !!deltagerId.value)
        const showDeltagerError = computed(() => (isSubmitted.value || hasInteractedDeltager.value) && !isDeltagerIdValid.value)

        // ── Overall visibility ────────────────────────────────────
        const showSupportType = computed(() => supportStafet.value && stafetkode.value)
        const showHold = computed(() => supportStafet.value && needsHold.value)
        const showDeltager = computed(() => supportStafet.value && needsDeltager.value)

        // ── Hidden input ──────────────────────────────────────────
        const updateHiddenInput = () => {
            const input = document.getElementById(props.elementId)
            if (!input) return
            input.value = JSON.stringify({
                [props.fieldNames.supportStafet]: supportStafet.value,
                [props.fieldNames.supportType]: supportType.value,
                [props.fieldNames.stafetkode]: stafetkode.value,
                [props.fieldNames.holdkode]: holdkode.value,
                [props.fieldNames.deltagerId]: deltagerId.value
            })
        }

        // ── API ───────────────────────────────────────────────────
        const fetchStafetter = async () => {
            try {
                const pageId = store.state.main.page_id
                const url = `${document.location.origin}/${process.env.GET_RELAY_LIST.replace('[pageId]', pageId)}?take=100`
                const response = await globalFetch(url)
                const data = await response.json()
                stafetter.value = data.hits || []
            } catch (err) {
                console.error(err)
            }
        }

        const fetchHold = async (kode) => {
            if (!kode) {
                hold.value = []
                return
            }
            try {
                const pageId = store.state.main.page_id
                const url = `${document.location.origin}/${process.env.GET_STAFET_TEAM_LIST
                    .replace('[stafetId]', kode)
                    .replace('[pageId]', pageId)}`
                const response = await globalFetch(url)
                const data = await response.json()
                hold.value = data.hits || []
            } catch (err) {
                console.error(err)
            }
        }

        const fetchDeltagere = async (kode) => {
            if (!kode) {
                deltagere.value = []
                return
            }
            try {
                const url = `${document.location.origin}/${process.env.GET_STAFET_PARTICIPANTS.replace('[teamId]', kode)}`
                const response = await globalFetch(url)
                const data = await response.json()
                deltagere.value = data.hits || []
            } catch (err) {
                console.error(err)
            }
        }

        // ── Handlers ──────────────────────────────────────────────
        const handleCheckboxChange = () => {
            if (!supportStafet.value) {
                // Reset all downstream state when unchecking
                searchQuery.value = ''
                selectedStafet.value = null
                supportType.value = SUPPORT_TYPE.NONE
                holdkode.value = null
                deltagerId.value = null
                hold.value = []
                deltagere.value = []
                hasInteractedStafet.value = false
                hasInteractedSupportType.value = false
                hasInteractedHold.value = false
                hasInteractedDeltager.value = false
            }
            updateHiddenInput()
        }

        const handleClearSearch = () => {
            searchQuery.value = ''
            selectedStafet.value = null
            supportType.value = SUPPORT_TYPE.NONE
            holdkode.value = null
            deltagerId.value = null
            hold.value = []
            deltagere.value = []
            showDropdown.value = false
            updateHiddenInput()
        }

        const handleSearchInput = () => {
            showDropdown.value = true
            // Clear selection if user edits the text after choosing
            if (selectedStafet.value && searchQuery.value !== selectedStafet.value.name) {
                selectedStafet.value = null
                supportType.value = SUPPORT_TYPE.NONE
                holdkode.value = null
                deltagerId.value = null
                hold.value = []
                deltagere.value = []
                updateHiddenInput()
            }
        }

        const handleSearchFocus = () => {
            showDropdown.value = true
        }

        const handleStafetSelect = (stafet) => {
            selectedStafet.value = stafet
            searchQuery.value = stafet.name
            showDropdown.value = false
            hasInteractedStafet.value = true
            // Reset downstream and pre-fetch hold
            supportType.value = SUPPORT_TYPE.STAFET
            holdkode.value = null
            deltagerId.value = null
            deltagere.value = []
            fetchHold(stafet.stafetKode)
            updateHiddenInput()
        }

        const handleSupportTypeChange = (e) => {
            const newType = e.target.value || SUPPORT_TYPE.NONE
            supportType.value = newType
            hasInteractedSupportType.value = true

            // Only wipe hold state when switching to a type that doesn't need it;
            // preserve it when moving between Hold ↔ Deltager so no spurious error appears.
            const newNeedsHold = newType === SUPPORT_TYPE.HOLD || newType === SUPPORT_TYPE.DELTAGER
            if (!newNeedsHold) {
                holdkode.value = null
                hold.value = []
                hasInteractedHold.value = false
            }

            deltagerId.value = null
            deltagere.value = []
            hasInteractedDeltager.value = false

            // If switching to Deltager with a team already chosen, fetch participants immediately
            if (newType === SUPPORT_TYPE.DELTAGER && holdkode.value) {
                fetchDeltagere(holdkode.value)
            }

            updateHiddenInput()
        }

        const handleSupportTypeBlur = () => {
            hasInteractedSupportType.value = true
        }

        const handleHoldChange = (e) => {
            holdkode.value = e.target.value || null
            hasInteractedHold.value = true
            deltagerId.value = null
            fetchDeltagere(holdkode.value)
            updateHiddenInput()
        }

        const handleHoldBlur = () => {
            hasInteractedHold.value = true
        }

        const handleDeltagerChange = (e) => {
            deltagerId.value = e.target.value || null
            hasInteractedDeltager.value = true
            updateHiddenInput()
        }

        const handleDeltagerBlur = () => {
            hasInteractedDeltager.value = true
        }

        // ── Click outside to close stafet dropdown ─────────────────
        const handleClickOutside = (e) => {
            if (searchContainer.value && !searchContainer.value.contains(e.target)) {
                showDropdown.value = false
            }

            if (e.target.classList.contains('FormSubmitButton')) {
                isSubmitted.value = true
            }
        }

        onMounted(() => {
            fetchStafetter()
            updateHiddenInput()
            document.addEventListener('click', handleClickOutside, true)
        })

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside, true)
        })

        return {
            SUPPORT_TYPE,

            // checkbox
            supportStafet,

            // stafet search
            searchQuery,
            searchContainer,
            selectedStafet,
            showDropdown,
            filteredStafetter,
            showStafetError,

            // support type
            supportType,
            supportTypeRadioOptions,
            showSupportType,
            showSupportTypeError,

            // hold
            hold,
            holdkode,
            showHold,
            showHoldError,

            // deltager
            deltagere,
            deltagerId,
            showDeltager,
            showDeltagerError,

            // handlers
            handleCheckboxChange,
            handleClearSearch,
            handleSearchInput,
            handleSearchFocus,
            handleStafetSelect,
            handleSupportTypeChange,
            handleSupportTypeBlur,
            handleHoldChange,
            handleHoldBlur,
            handleDeltagerChange,
            handleDeltagerBlur
        }
    }
}
